'use client';

// Webhook trigger editor for a managed agent. Lists existing triggers (source,
// description, active toggle, inbound URL, regenerate/remove), and an "add
// webhook" form. Secrets are redacted after mint, so we only ever show a secret
// in the just-created / just-regenerated state — a one-time copyable panel with
// a "save this now" warning + an HMAC-signed example curl.

import { useEffect, useMemo, useState } from 'react';
import {
  Lightning,
  Plus,
  Trash,
  ArrowsClockwise,
  Copy,
  Check,
  WarningCircle,
  X,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import type { AgentEventTriggerSummary, AgentSummary } from './types';

/** Freshly-minted secret (create or regenerate) — shown to the owner ONCE. */
interface MintedSecret {
  triggerId: string;
  secret: string;
  inboundUrl: string;
  description?: string | null;
}

/** Small copy-to-clipboard button matching the /agents phosphor design. */
function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Copy blocked — select the text and copy manually.');
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      aria-label={copied ? 'Copied' : label || 'Copy'}
      className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border/60 px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:border-border hover:text-foreground"
    >
      {copied ? <Check weight="bold" className="size-3 text-emerald-400" /> : <Copy weight="bold" className="size-3" />}
      {copied ? 'Copied' : label || 'Copy'}
    </button>
  );
}

/** Parse the filter textarea (one `key=value` per line) into a flat object. */
function parseFilter(raw: string): { ok: true; value: Record<string, unknown> | null } | { ok: false; error: string } {
  const lines = raw
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return { ok: true, value: null };
  const out: Record<string, unknown> = {};
  for (const line of lines) {
    const eq = line.indexOf('=');
    if (eq <= 0) return { ok: false, error: `Each line must be key=value — got "${line}"` };
    const key = line.slice(0, eq).trim();
    const rawVal = line.slice(eq + 1).trim();
    if (!key) return { ok: false, error: `Missing key in "${line}"` };
    // Try JSON (numbers/booleans/quoted strings); fall back to a plain string.
    let val: unknown = rawVal;
    try {
      val = JSON.parse(rawVal);
    } catch {
      val = rawVal;
    }
    out[key] = val;
  }
  return { ok: true, value: out };
}

function exampleCurl(inboundUrl: string, secret: string): string {
  return [
    `BODY='{"hello":"world"}'`,
    `SIG=$(printf '%s' "$BODY" | openssl dgst -sha256 -hmac "${secret}" | sed 's/^.* //')`,
    `curl -X POST "${inboundUrl}" \\`,
    `  -H "content-type: application/json" \\`,
    `  -H "x-galexia-event-signature: $SIG" \\`,
    `  -d "$BODY"`,
  ].join('\n');
}

export function AgentTriggersDialog({
  open,
  onOpenChange,
  agent,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agent: AgentSummary | null;
  onChanged: () => void;
}) {
  const agentId = agent?.id ?? '';
  const [triggers, setTriggers] = useState<AgentEventTriggerSummary[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [minted, setMinted] = useState<MintedSecret | null>(null);
  const [removeTarget, setRemoveTarget] = useState<AgentEventTriggerSummary | null>(null);

  // Add form
  const [showForm, setShowForm] = useState(false);
  const [formDesc, setFormDesc] = useState('');
  const [formFilter, setFormFilter] = useState('');
  const [adding, setAdding] = useState(false);

  // Fallback inbound URL for existing triggers (secret already redacted). The
  // server returns the authoritative URL on mint; before that we build it from
  // the browser origin so it matches the host the owner is on.
  const baseInboundUrl = useMemo(
    () => (typeof window !== 'undefined' && agentId ? `${window.location.origin}/api/agents/${agentId}/events` : ''),
    [agentId],
  );

  // Seed only when the dialog opens or the target agent changes — NOT on every
  // parent refetch (onChanged), so the one-time secret panel survives the
  // refetch that immediately follows a create/regenerate.
  useEffect(() => {
    if (open) {
      setTriggers(agent?.eventTriggers ?? []);
      setMinted(null);
      setShowForm(false);
      setFormDesc('');
      setFormFilter('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, agentId]);

  const handleAdd = async () => {
    const parsedFilter = parseFilter(formFilter);
    if (!parsedFilter.ok) {
      toast.error(parsedFilter.error);
      return;
    }
    setAdding(true);
    try {
      const res = await fetch(`/api/agents/${agentId}/triggers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: 'webhook',
          description: formDesc.trim() || null,
          filter: parsedFilter.value,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(typeof data?.error === 'string' ? data.error : `Add failed (${res.status})`);
      const trigger = data.trigger as AgentEventTriggerSummary & { secret: string };
      setTriggers((prev) => [
        ...prev,
        { id: trigger.id, source: trigger.source, active: trigger.active, description: trigger.description, filter: trigger.filter, secretSet: true },
      ]);
      setMinted({ triggerId: trigger.id, secret: trigger.secret, inboundUrl: data.inboundUrl, description: trigger.description });
      setShowForm(false);
      setFormDesc('');
      setFormFilter('');
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Add failed');
    } finally {
      setAdding(false);
    }
  };

  const handleToggle = async (trigger: AgentEventTriggerSummary, active: boolean) => {
    setTriggers((prev) => prev.map((t) => (t.id === trigger.id ? { ...t, active } : t)));
    setBusyId(trigger.id);
    try {
      const res = await fetch(`/api/agents/${agentId}/triggers/${trigger.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active }),
      });
      if (!res.ok) throw new Error(`Update failed (${res.status})`);
      onChanged();
    } catch (err) {
      setTriggers((prev) => prev.map((t) => (t.id === trigger.id ? { ...t, active: !active } : t)));
      toast.error(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleRegenerate = async (trigger: AgentEventTriggerSummary) => {
    setBusyId(trigger.id);
    try {
      const res = await fetch(`/api/agents/${agentId}/triggers/${trigger.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ regenerate: true }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(typeof data?.error === 'string' ? data.error : `Regenerate failed (${res.status})`);
      const t = data.trigger as AgentEventTriggerSummary & { secret: string };
      setMinted({ triggerId: t.id, secret: t.secret, inboundUrl: data.inboundUrl, description: t.description });
      toast.success('New secret minted — the old one no longer works.');
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Regenerate failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleRemoveConfirm = async () => {
    const trigger = removeTarget;
    if (!trigger) return;
    setRemoveTarget(null);
    setTriggers((prev) => prev.filter((t) => t.id !== trigger.id));
    if (minted?.triggerId === trigger.id) setMinted(null);
    try {
      const res = await fetch(`/api/agents/${agentId}/triggers/${trigger.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Remove failed (${res.status})`);
      toast.success('Trigger removed');
      onChanged();
    } catch (err) {
      setTriggers((prev) => [...prev, trigger]);
      toast.error(err instanceof Error ? err.message : 'Remove failed');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightning weight="duotone" className="size-5 text-amber-400" />
              Event triggers
            </DialogTitle>
            <DialogDescription>
              Wake {agent ? `“${agent.name}”` : 'this agent'} from an external source. Each trigger has its own
              signed inbound URL — POST a signed event and the agent runs, budget-gated like any wake.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {/* One-time secret panel */}
            {minted && (
              <OneTimeSecretPanel minted={minted} baseInboundUrl={baseInboundUrl} onDismiss={() => setMinted(null)} />
            )}

            {/* Existing triggers */}
            {triggers.length === 0 && !showForm && (
              <p className="rounded-lg border border-dashed border-border/60 px-3 py-6 text-center text-xs text-muted-foreground">
                No triggers yet. Add a webhook to let an external source wake this agent.
              </p>
            )}

            {triggers.map((t) => (
              <div key={t.id} className="rounded-lg border border-border/60 px-3 py-2.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-amber-500/12 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-400">
                        {t.source}
                      </span>
                      <span className={`text-[11px] font-medium ${t.active ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                        {t.active ? 'Active' : 'Disabled'}
                      </span>
                    </div>
                    <p className="mt-1 truncate text-xs text-foreground/90">
                      {t.description || <span className="text-muted-foreground italic">No description</span>}
                    </p>
                    {t.filter && Object.keys(t.filter).length > 0 && (
                      <p className="mt-0.5 truncate font-mono text-[10.5px] text-muted-foreground">
                        filter: {JSON.stringify(t.filter)}
                      </p>
                    )}
                  </div>
                  <Switch
                    checked={t.active}
                    disabled={busyId === t.id}
                    onCheckedChange={(v) => handleToggle(t, v)}
                    aria-label={t.active ? 'Disable trigger' : 'Enable trigger'}
                  />
                </div>

                {/* Inbound URL */}
                <div className="mt-2 flex items-center gap-2">
                  <code className="min-w-0 flex-1 truncate rounded-md bg-muted/50 px-2 py-1 font-mono text-[10.5px] text-muted-foreground">
                    {baseInboundUrl}
                  </code>
                  <CopyButton value={baseInboundUrl} label="URL" />
                </div>

                {/* Actions */}
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleRegenerate(t)}
                    disabled={busyId === t.id}
                    className="inline-flex items-center gap-1 rounded-md border border-border/60 px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:border-border hover:text-foreground disabled:opacity-50"
                  >
                    <ArrowsClockwise weight="bold" className={`size-3 ${busyId === t.id ? 'animate-spin' : ''}`} />
                    Regenerate secret
                  </button>
                  <button
                    type="button"
                    onClick={() => setRemoveTarget(t)}
                    className="ml-auto inline-flex items-center gap-1 rounded-md border border-border/60 px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:border-rose-400/50 hover:text-rose-400"
                  >
                    <Trash weight="bold" className="size-3" />
                    Remove
                  </button>
                </div>
              </div>
            ))}

            {/* Add form */}
            {showForm ? (
              <div className="space-y-2.5 rounded-lg border border-border/60 bg-muted/20 px-3 py-3">
                <p className="text-xs font-semibold text-foreground/90">New webhook trigger</p>
                <div>
                  <p className="mb-1 text-[11px] font-medium text-muted-foreground">Description (optional)</p>
                  <Input
                    value={formDesc}
                    onChange={(e) => setFormDesc(e.target.value)}
                    placeholder="e.g. GitHub push events"
                    maxLength={200}
                    className="text-xs"
                  />
                </div>
                <div>
                  <p className="mb-1 text-[11px] font-medium text-muted-foreground">
                    Filter (optional) — one <code className="font-mono">key=value</code> per line; all must match the payload
                  </p>
                  <Textarea
                    value={formFilter}
                    onChange={(e) => setFormFilter(e.target.value)}
                    placeholder={'type=push\naction="opened"'}
                    className="min-h-[64px] font-mono text-xs"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-0.5">
                  <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    disabled={adding}
                    onClick={handleAdd}
                    className="rounded-full bg-violet-500 px-4 font-semibold text-white hover:bg-violet-400"
                  >
                    {adding ? 'Creating…' : 'Create trigger'}
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowForm(true)}
                className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border/60 px-3 py-2.5 text-xs font-medium text-muted-foreground transition-colors hover:border-border hover:text-foreground"
              >
                <Plus weight="bold" className="size-3.5" />
                Add webhook trigger
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(removeTarget)} onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}>
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove trigger?</AlertDialogTitle>
            <AlertDialogDescription>
              Its inbound URL will stop working immediately. Any source posting to it will get a 401. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={() => void handleRemoveConfirm()}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function OneTimeSecretPanel({
  minted,
  baseInboundUrl,
  onDismiss,
}: {
  minted: MintedSecret;
  baseInboundUrl: string;
  onDismiss: () => void;
}) {
  const url = minted.inboundUrl || baseInboundUrl;
  const curl = exampleCurl(url, minted.secret);
  return (
    <div className="relative rounded-xl border border-amber-400/40 bg-amber-400/[0.06] px-3.5 py-3">
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="absolute right-2 top-2 grid size-6 place-items-center rounded-md text-muted-foreground transition-colors hover:text-foreground"
      >
        <X weight="bold" className="size-3.5" />
      </button>
      <div className="flex items-center gap-2">
        <WarningCircle weight="fill" className="size-4 text-amber-400" />
        <p className="text-xs font-bold text-amber-300">Save this secret now — it is shown only once</p>
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">
        We store the secret to verify signatures, but never show it again. Regenerate if you lose it.
      </p>

      <div className="mt-2.5 space-y-2">
        <div>
          <p className="mb-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">Inbound URL</p>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-md bg-background/60 px-2 py-1.5 font-mono text-[11px]">{url}</code>
            <CopyButton value={url} label="Copy" />
          </div>
        </div>
        <div>
          <p className="mb-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">Signing secret</p>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-md bg-background/60 px-2 py-1.5 font-mono text-[11px]">{minted.secret}</code>
            <CopyButton value={minted.secret} label="Copy" />
          </div>
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between">
            <p className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">Example (HMAC-signed curl)</p>
            <CopyButton value={curl} label="Copy" />
          </div>
          <pre className="overflow-x-auto rounded-md bg-background/60 px-2.5 py-2 font-mono text-[10.5px] leading-relaxed text-muted-foreground">
{curl}
          </pre>
        </div>
      </div>
    </div>
  );
}
